'use client';

import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@pickleballinc/ui/components/ui/button';
import { Panel, PanelContent, PanelHeader, PanelBody, PanelClose, PanelDescription, PanelTitle } from '@pickleballinc/ui/components/ui/panel';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { formatTeamLeagueLabels } from '@/utils/helpers';

interface PrintMatchesModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onPrint: (data: PrintFormType) => void;
	hideSheetSize?: boolean;
}

const printFormSchema = z.object({
	sheetType: z.enum(['official', 'simplified', 'mlp']),
	sheetSize: z.enum(['half', 'full'])
});

type PrintFormType = z.infer<typeof printFormSchema>;

export const PrintMatchesModal = ({ open, onOpenChange, onPrint, hideSheetSize = false }: PrintMatchesModalProps) => {
	const teamLeagueInfo = useTeamLeagueInfo();
	const labels = formatTeamLeagueLabels(teamLeagueInfo);

	const form = useForm<PrintFormType>({
		resolver: zodResolver(printFormSchema),
		defaultValues: {
			sheetType: 'official',
			sheetSize: 'half'
		}
	});

	const sheetType = form.watch('sheetType');

	// Automatically set Half Sheets when MLP is selected
	useEffect(() => {
		if (sheetType === 'mlp') {
			form.setValue('sheetSize', 'half');
		}
	}, [sheetType, form]);

	const handlePrint = form.handleSubmit((data: PrintFormType) => {
		onPrint(data);
		onOpenChange(false);
	});

	return (
		<FormProvider {...form}>
			<Panel open={open} onOpenChange={onOpenChange}>
				<PanelContent className="flex w-full flex-col md:w-96 xl:w-100 2xl:w-120" aria-describedby="print-matches-description">
					<PanelHeader className="flex flex-col px-6 pb-2 pt-4 border-b border-gray-200">
						<div className="flex items-center justify-between">
							<PanelTitle asChild>
								<h2 className="text-2xl font-semibold text-gray-900">Print Matches</h2>
							</PanelTitle>
							<PanelClose asChild>
								<button
									className="size-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
									aria-label="Close dialog"
								>
									<FontAwesomeIcon icon={faXmark} className="size-5 text-gray-700" />
								</button>
							</PanelClose>
						</div>
						<PanelDescription className="text-sm font-normal text-gray-600 text-start">Select sheet type to print</PanelDescription>
					</PanelHeader>

					<PanelBody className="flex-1 px-6 pt-4 pb-8">
						<div className="flex h-full flex-col">
							<div className="flex-1 space-y-6">
								<div className="flex flex-col gap-2">
									<h3 className="text-lg font-semibold text-gray-900">All {labels.matchupSingularTitle} Scoresheets</h3>
								</div>

								<div className="flex flex-col gap-4">
									<span className="text-sm font-medium text-gray-700">Select sheet type</span>
									<div className="space-y-3">
										<label className="flex items-center gap-3 cursor-pointer">
											<input
												{...form.register('sheetType')}
												type="radio"
												value="official"
												className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 accent-orange-500"
											/>
											<span className="text-sm text-gray-900">Official Score Sheet</span>
										</label>
										<label className="flex items-center gap-3 cursor-pointer">
											<input
												{...form.register('sheetType')}
												type="radio"
												value="simplified"
												className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 accent-orange-500"
											/>
											<span className="text-sm text-gray-900">Simplified Score Sheet</span>
										</label>
										<label className="flex items-center gap-3 cursor-pointer">
											<input
												{...form.register('sheetType')}
												type="radio"
												value="mlp"
												className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 accent-orange-500"
											/>
											<span className="text-sm text-gray-900">MLP Score Sheet</span>
										</label>
									</div>
								</div>

								{!hideSheetSize && (
									<div className="flex flex-col gap-4">
										<span className="text-sm font-medium text-gray-700">Select sheet size</span>
										<div className="space-y-3">
											<label className="flex items-center gap-3 cursor-pointer">
												<input
													{...form.register('sheetSize')}
													type="radio"
													value="half"
													className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 accent-orange-500"
												/>
												<span className="text-sm text-gray-900">Half Sheets</span>
											</label>
											<div className="relative">
												<label
													className={`flex items-center gap-3 ${sheetType === 'mlp' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
												>
													<input
														{...form.register('sheetSize')}
														type="radio"
														value="full"
														disabled={sheetType === 'mlp'}
														className={`w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500 accent-orange-500 ${
															sheetType === 'mlp' ? 'cursor-not-allowed' : ''
														}`}
													/>
													<span className="text-sm text-gray-900">Full Sheets</span>
													{sheetType === 'mlp' && (
														<span className="text-xs text-gray-500 italic">(Not available for MLP format)</span>
													)}
												</label>
											</div>
										</div>
									</div>
								)}
							</div>

							<div className="flex flex-row gap-4">
								<Button
									onClick={() => onOpenChange(false)}
									className="!w-full justify-center text-base font-semibold shadow-xs transition-all duration-200 hover:shadow-sm"
									variant="outline"
								>
									Close
								</Button>
								<Button
									onClick={handlePrint}
									className="w-full border border-gray-200 text-base font-semibold shadow-xs transition-all duration-200 hover:shadow-sm"
								>
									Print
								</Button>
							</div>
						</div>
					</PanelBody>
				</PanelContent>
			</Panel>
		</FormProvider>
	);
};
